import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { validateAssetSuggestion } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AISuggestionReviewColumnData } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewCell';
import { AISuggestionReviewRow } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewRow';
import { useAISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { useAssetCategoryOptions } from '@/modules/entities/StructuredAssetsSubform/hooks/useAssetCategoryOptions';
import { formatCurrency } from '@/utils/formatting/currency';

interface AISuggestionReviewAssetRowProps {
  suggestion: AiSuggestionForReviewCard;
}

export function AISuggestionReviewAssetRow({
  suggestion,
}: AISuggestionReviewAssetRowProps) {
  const { onReviewClick, onDeleteClick, onCreateClick } =
    useAISuggestionReviewCardContext();

  const { options: assetCategoryOptions } = useAssetCategoryOptions();

  const { columnsData, hasWarning } = useMemo<{
    columnsData: AISuggestionReviewColumnData[];
    hasWarning: boolean;
  }>(() => {
    const s = suggestion.asset;
    if (!s) return { columnsData: [], hasWarning: false };

    const { nameMissing, categoryMissing, valueMissing, hasWarning } =
      validateAssetSuggestion(suggestion, assetCategoryOptions);

    const nameText = s.assetTitle || '';
    const categoryDisplay = assetCategoryOptions.find(
      (o) => o.value === s.assetCategoryID
    )?.display;

    // Format the asset value if it exists
    const valueText = s.assetValue
      ? formatCurrency(new Decimal(s.assetValue))
      : '';

    const warnNameTooltip = nameMissing
      ? 'Missing asset name. Review required.'
      : undefined;
    const warnCategoryTooltip = categoryMissing
      ? 'Missing asset category. Review required.'
      : undefined;
    const warnValueTooltip = valueMissing
      ? 'Missing asset value. Review required.'
      : undefined;

    const columns: AISuggestionReviewColumnData[] = [
      {
        label: 'Name',
        values: [
          {
            displayText: nameText || '-',
            showWarning: nameMissing,
            warningTooltip: warnNameTooltip,
          },
        ],
      },
      {
        label: 'Asset category',
        values: [
          {
            displayText: categoryDisplay || '-',
            showWarning: categoryMissing,
            warningTooltip: warnCategoryTooltip,
          },
        ],
      },
      {
        label: 'Value',
        values: [
          {
            displayText: valueText || '-',
            showWarning: valueMissing,
            warningTooltip: warnValueTooltip,
          },
        ],
      },
    ];

    return { columnsData: columns, hasWarning };
  }, [assetCategoryOptions, suggestion]);

  if (!suggestion.asset) return null;

  return (
    <AISuggestionReviewRow
      suggestionID={suggestion.id}
      rowHasWarning={hasWarning}
      columnsData={columnsData}
      onCreateClick={onCreateClick}
      onReviewClick={onReviewClick}
      onDeleteClick={onDeleteClick}
      // Align with 3 info columns (Name, Category, Value)
      columnCount={3}
    />
  );
}
